import {
    gqlIncidents,
    processDefinitionQueryIncidentFragment,
    useProcessDefinitionIncidentsLoader,
    useProcessDefinitionQueryFragment,
} from './process-definition-query.ts';
import { Suspense, useEffect, useState } from 'react';
import {
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
} from '@fluentui/react-components';
import { type PreloadedQuery, useFragment, usePreloadedQuery } from 'react-relay';
import type { processDefinitionQueryIncidentFragment$key } from './__generated__/processDefinitionQueryIncidentFragment.graphql.ts';
import type { processDefinitionQueryIncidentsQuery } from './__generated__/processDefinitionQueryIncidentsQuery.graphql.ts';

const dtf = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'medium' });
const formatTs = (ts?: string | null) => (ts ? dtf.format(new Date(ts)) : '');

const useStyles = makeStyles({
    surface: { maxWidth: '1000px', width: '90vw' },
    timeCell: { whiteSpace: 'nowrap', width: '180px' },
    typeCell: { width: '120px' },
    activityCell: { width: '160px' },
    bold: { fontWeight: 'bold' },
    message: {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        cursor: 'help',
    },
});

let registeredTrigger: (() => void) | null = null;
export const registerIncidentsTrigger = (fn: (() => void) | null) => {
    registeredTrigger = fn;
};
export const triggerIncidentsDialog = () => {
    if (!registeredTrigger) {
        console.warn('IncidentsDialog ist nicht gemountet');
        return;
    }
    registeredTrigger();
};

export const IncidentsDialog = () => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const definitionId = queryFragment?.definitionId;
    const [queryRef, loadQuery, disposeQuery] = useProcessDefinitionIncidentsLoader();
    const [open, setOpen] = useState(false);
    const styles = useStyles();

    useEffect(() => {
        registeredTrigger = () => {
            if (!definitionId) return; // z. B. noch nicht veröffentlicht
            loadQuery({ definitionId }, { fetchPolicy: 'network-only' });
            setOpen(true);
        };
        return () => {
            registeredTrigger = null;
        };
    }, [loadQuery, definitionId]);

    return (
        <Dialog
            open={open}
            onOpenChange={(_, d) => {
                setOpen(d.open);
                if (!d.open) disposeQuery();
            }}
        >
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>Fehler Log</DialogTitle>
                    <DialogContent>
                        <Suspense fallback={<Spinner label="Lade Incidents…" />}>
                            {queryRef && <IncidentsTable queryRef={queryRef} />}
                        </Suspense>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={() => setOpen(false)}>
                            Schließen
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

const IncidentsTable = ({
    queryRef,
}: {
    queryRef: PreloadedQuery<processDefinitionQueryIncidentsQuery>;
}) => {
    const { getProcessDefinitionIncidents } = usePreloadedQuery(gqlIncidents, queryRef);
    const styles = useStyles();

    if (!getProcessDefinitionIncidents?.length) return <>Keine Incidents vorhanden.</>;

    return (
        <Table size="small">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell className={styles.timeCell && styles.bold}>
                        Zeitpunkt
                    </TableHeaderCell>
                    <TableHeaderCell className={styles.typeCell && styles.bold}>
                        Typ
                    </TableHeaderCell>
                    <TableHeaderCell className={styles.activityCell && styles.bold}>
                        Aktivität
                    </TableHeaderCell>
                    <TableHeaderCell className={styles.bold}>Meldung</TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {getProcessDefinitionIncidents.map((i) => (
                    <IncidentRow key={i.id} incidentRef={i} styles={styles} />
                ))}
            </TableBody>
        </Table>
    );
};

const IncidentRow = ({
    incidentRef,
    styles,
}: {
    incidentRef: processDefinitionQueryIncidentFragment$key;
    styles: ReturnType<typeof useStyles>;
}) => {
    const inc = useFragment(processDefinitionQueryIncidentFragment, incidentRef);
    return (
        <TableRow>
            <TableCell className={styles.timeCell}>{formatTs(inc.incidentTimestamp)}</TableCell>
            <TableCell className={styles.typeCell}>
                <Badge
                    appearance="tint"
                    color={inc.incidentType === 'failedJob' ? 'danger' : 'warning'}
                >
                    {inc.incidentType}
                </Badge>
            </TableCell>
            <TableCell className={styles.activityCell}>{inc.activityId}</TableCell>
            <TableCell>
                <Tooltip content={inc.incidentMessage ?? ''} relationship="description" withArrow>
                    <span className={styles.message}>{inc.incidentMessage}</span>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};
